import { useRef, useState } from 'react'
import { AlertCircle, Check, Download, FileSpreadsheet, Loader2, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { FichierIllisible, lireTableur, type LigneTableur } from '@/lib/lireTableur'
import { useImportContacts, type ImportContactsResult } from '@/api/contacts'
import { useApiError } from '@/hooks/useApiError'

const MAX_LIGNES = 5000

// Le fichier d'exemple : les en-têtes que le serveur reconnaît, et des contacts
// fictifs (numéros 555-01xx, réservés à la fiction) pour montrer chaque colonne.
const MODELE = [
  ['telephone', 'prenom', 'nom', 'courriel', 'entreprise', 'poste', 'adresse', 'ville', 'province', 'code postal', 'pays', 'site web', 'telephone 2', 'statut', 'etiquettes'],
  ['514 555-0101', 'Marie', 'Tremblay', 'marie@exemple.ca', 'Tremblay Rénovation', 'Propriétaire', '1234 rue Saint-Denis', 'Montréal', 'QC', 'H2X 3K2', 'Canada', 'tremblay-renovation.ca', '514 555-0199', 'client', 'VIP, rénovation'],
  ['438 555-0102', 'Jean', 'Gagnon', 'jean.gagnon@exemple.ca', 'Garage Gagnon', 'Gérant', '56 boulevard Taschereau', 'Longueuil', 'QC', 'J4K 2V6', 'Canada', '', '', 'prospect', 'garage'],
  ['418 555-0103', 'Sophie', 'Roy', '', '', '', '789 avenue Cartier', 'Québec', 'QC', 'G1R 2S5', 'Canada', '', '', 'lead', ''],
  ['613 555-0104', 'David', 'Nguyen', 'd.nguyen@exemple.ca', 'Nguyen Comptables', 'Associé', '45 rue Sparks', 'Ottawa', 'ON', 'K1P 5B7', 'Canada', 'nguyen-cpa.ca', '', 'client', 'comptabilité'],
  ['450 555-0105', 'Amina', 'Diallo', 'amina@exemple.ca', '', '', '', 'Laval', 'QC', '', 'Canada', '', '', 'prospect', 'site web'],
]

function telechargerModele() {
  const csv = MODELE.map((l) => l.map((v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)).join(',')).join('\n')
  // BOM : Excel lit alors les accents correctement.
  const lien = document.createElement('a')
  lien.href = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' }))
  lien.download = 'exemple-contacts-callizy.csv'
  lien.click()
  URL.revokeObjectURL(lien.href)
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Import de contacts : choisir un fichier, voir ce qui va se passer, confirmer.
 * Rien n'est écrit avant « Importer » : l'aperçu est calculé par le serveur
 * sans enregistrer (dry_run).
 */
export function ImportContactsDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation()
  const { handleError } = useApiError()
  const importer = useImportContacts()
  const entree = useRef<HTMLInputElement>(null)
  const [fichier, setFichier] = useState<File | null>(null)
  const [lignes, setLignes] = useState<LigneTableur[]>([])
  const [apercu, setApercu] = useState<ImportContactsResult | null>(null)
  const [lecture, setLecture] = useState(false)
  const [survol, setSurvol] = useState(false)
  // Colonnes inconnues que le gestionnaire garde comme propriétés de fiche.
  const [garder, setGarder] = useState<string[]>([])
  // Les colonnes proposées restent visibles même cochées (elles quittent alors ignored_columns).
  const [proposees, setProposees] = useState<string[]>([])

  const reset = () => {
    setFichier(null)
    setLignes([])
    setApercu(null)
    setGarder([])
    setProposees([])
  }

  const basculer = async (colonne: string) => {
    const suivant = garder.includes(colonne) ? garder.filter((c) => c !== colonne) : [...garder, colonne]
    setGarder(suivant)
    try {
      setApercu(await importer.mutateAsync({ rows: lignes, dry_run: true, new_properties: suivant }))
    } catch (e) {
      handleError(e)
    }
  }

  const fermer = () => {
    if (importer.isPending) return
    reset()
    onOpenChange(false)
  }

  const choisir = async (f: File | undefined) => {
    if (!f) return
    reset()
    setFichier(f)
    setLecture(true)
    try {
      const lues = await lireTableur(f)
      if (!lues.length) {
        toast.error(t('contacts.import.empty', 'Le fichier ne contient aucune ligne.'))
        reset()
        return
      }
      if (lues.length > MAX_LIGNES) {
        toast.error(t('contacts.import.tooMany', '{{max}} lignes au plus par fichier.', { max: MAX_LIGNES.toLocaleString() }))
        reset()
        return
      }
      setLignes(lues)
      const premier = await importer.mutateAsync({ rows: lues, dry_run: true })
      setApercu(premier)
      setProposees(premier.ignored_columns)
    } catch (e) {
      if (e instanceof FichierIllisible) {
        toast.error(t('contacts.import.unreadable', 'Ce fichier ne peut pas être lu. Enregistrez-le en .csv ou .xlsx.'))
      } else {
        handleError(e)
      }
      reset()
    } finally {
      setLecture(false)
    }
  }

  const confirmer = async () => {
    try {
      const fait = await importer.mutateAsync({ rows: lignes, dry_run: false, new_properties: garder })
      toast.success(
        t('contacts.import.done', '{{created}} contacts ajoutés, {{completed}} complétés.', {
          created: fait.to_create,
          completed: fait.to_complete,
        }),
      )
      reset()
      onOpenChange(false)
    } catch (e) {
      handleError(e)
    }
  }

  const aFaire = apercu ? apercu.to_create + apercu.to_complete : 0

  return (
    <Dialog open={open} onOpenChange={fermer}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('contacts.import.title', 'Importer des contacts')}</DialogTitle>
          <DialogDescription>
            {t('contacts.import.subtitle', 'Un fichier CSV ou Excel, une ligne par contact. Le téléphone est obligatoire.')}
          </DialogDescription>
        </DialogHeader>

        {!apercu ? (
          <div className="space-y-3">
            <label
              onDragOver={(e) => {
                e.preventDefault()
                setSurvol(true)
              }}
              onDragLeave={() => setSurvol(false)}
              onDrop={(e) => {
                e.preventDefault()
                setSurvol(false)
                void choisir(e.dataTransfer.files[0])
              }}
              className={cn(
                'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors',
                survol
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                  : 'border-slate-200 hover:border-slate-400 dark:border-slate-700 dark:hover:border-slate-500',
              )}
            >
              <input
                ref={entree}
                type="file"
                accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="sr-only"
                onChange={(e) => {
                  void choisir(e.target.files?.[0])
                  e.target.value = ''
                }}
              />
              {lecture ? (
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              ) : (
                <Upload className="h-6 w-6 text-slate-400" />
              )}
              <span className="text-sm text-slate-600 dark:text-slate-300">
                {lecture
                  ? t('contacts.import.reading', 'Lecture de {{name}}…', { name: fichier?.name })
                  : t('contacts.import.drop', 'Glissez votre fichier ici ou cliquez pour le choisir.')}
              </span>
              <span className="text-xs text-slate-400">
                {t(
                  'contacts.import.columns',
                  'Colonnes : téléphone, prénom, nom, courriel, entreprise, poste, adresse, ville, province, code postal, pays, site web, téléphone 2, statut, étiquettes',
                )}
              </span>
            </label>
            <button
              type="button"
              onClick={telechargerModele}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              <Download className="h-3.5 w-3.5" />
              {t('contacts.import.template', 'Télécharger un fichier d’exemple')}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <FileSpreadsheet className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{fichier?.name}</span>
              <span className="text-slate-400 tabular-nums">
                · {t('contacts.import.rows', '{{count}} lignes', { count: lignes.length })}
              </span>
            </div>

            <ul className="space-y-1.5 text-sm tabular-nums">
              <Ligne valeur={apercu.to_create} texte={t('contacts.import.toCreate', 'nouveaux contacts')} fort />
              <Ligne
                valeur={apercu.to_complete}
                texte={t('contacts.import.toComplete', 'contacts existants complétés (rien n’est écrasé)')}
              />
              <Ligne valeur={apercu.already_there} texte={t('contacts.import.alreadyThere', 'déjà à jour dans le carnet')} />
              <Ligne valeur={apercu.duplicates_in_file} texte={t('contacts.import.duplicates', 'en double dans le fichier')} />
              <Ligne valeur={apercu.rejected} texte={t('contacts.import.rejected', 'lignes refusées')} alerte />
            </ul>

            {apercu.errors.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                <ul className="space-y-0.5 max-h-28 overflow-y-auto">
                  {apercu.errors.map((e) => (
                    <li key={e.ligne}>
                      {e.code === 'missing_phone'
                        ? t('contacts.import.errorMissing', 'Ligne {{line}} : aucun numéro', { line: e.ligne })
                        : t('contacts.import.errorInvalid', 'Ligne {{line}} : numéro invalide ({{value}})', {
                            line: e.ligne,
                            value: e.valeur,
                          })}
                    </li>
                  ))}
                </ul>
                {apercu.rejected > apercu.errors.length && (
                  <p className="mt-1 text-amber-700 dark:text-amber-300">
                    {t('contacts.import.moreErrors', 'et {{count}} autres', {
                      count: apercu.rejected - apercu.errors.length,
                    })}
                  </p>
                )}
              </div>
            )}

            {apercu.property_columns.length > 0 && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t('contacts.import.propertyColumns', 'Reprises dans vos propriétés : {{columns}}', {
                  columns: apercu.property_columns.join(', '),
                })}
              </p>
            )}

            {proposees.length > 0 && (
              <div className="space-y-1.5">
                <p className="flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  {t(
                    'contacts.import.unknownColumns',
                    'Ces colonnes ne correspondent à aucun champ. Cochez celles à garder : chacune devient une propriété de fiche.',
                  )}
                </p>
                <ul className="flex flex-wrap gap-1.5 pl-5">
                  {proposees.map((c) => {
                    const coche = garder.includes(c)
                    return (
                      <li key={c}>
                        <button
                          type="button"
                          role="checkbox"
                          aria-checked={coche}
                          disabled={importer.isPending}
                          onClick={() => void basculer(c)}
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors',
                            coche
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300'
                              : 'border-slate-200 text-slate-600 hover:border-slate-400 dark:border-slate-700 dark:text-slate-300',
                          )}
                        >
                          <span
                            className={cn(
                              'h-3 w-3 rounded-sm border flex items-center justify-center',
                              coche ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300 dark:border-slate-600',
                            )}
                          >
                            {coche && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
                          </span>
                          {c}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {apercu && (
            <Button type="button" variant="ghost" onClick={reset} disabled={importer.isPending}>
              {t('contacts.import.otherFile', 'Autre fichier')}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={fermer} disabled={importer.isPending}>
            {t('common.cancel', 'Annuler')}
          </Button>
          {apercu && (
            <Button type="button" onClick={confirmer} disabled={aFaire === 0 || importer.isPending}>
              {importer.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('contacts.import.confirm', 'Importer {{count}} contacts', { count: aFaire })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Ligne({ valeur, texte, fort, alerte }: { valeur: number; texte: string; fort?: boolean; alerte?: boolean }) {
  if (!valeur && !fort) return null
  return (
    <li className="flex items-baseline gap-2">
      <span
        className={cn(
          'min-w-[3ch] text-right font-semibold',
          alerte ? 'text-amber-600 dark:text-amber-400' : 'text-slate-900 dark:text-white',
        )}
      >
        {valeur.toLocaleString()}
      </span>
      <span className={cn(fort ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-300')}>{texte}</span>
    </li>
  )
}
